import { CSSProperties, useMemo } from "react";

import { ActionIcon, Box, Button, Flex, Group, Table, Text, Tooltip } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import { IconEdit } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Loader } from "components";
import { useCalendarEvents } from "hooks";
import { DashboardCalendarParams, DashboardCalendarResponse } from "types";
import { CalendarEventUtils, DateUtils } from "utils";

import classes from "./AbsencesTab.module.css";

interface UserSimple {
  id: number;
  fullName: string;
}

export interface AbsenceResponse {
  id: number;
  agent: UserSimple;
  replacementAgent?: UserSimple;
  dateFrom: string;
  dateTo: string;
  workingDays: number;
  reason?: string;
  note?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';
  createdOn?: string;
  createdBy?: UserSimple;
}

interface AbsencesTabProps {
  absences: AbsenceResponse[];
  loadCalendar: (params: DashboardCalendarParams) => Promise<DashboardCalendarResponse>;
  calendarRefreshKey?: number;
  onRequestClick: () => void;
  onAbsenceClick: (id: number) => void;
  onStatusEditClick?: (absence: AbsenceResponse) => void;
  loading?: boolean;
  readOnly?: boolean;
  isAdmin?: boolean;
}

const TABLE_MIN_WIDTH = 1100;

const tableInnerStyle: CSSProperties = {
  minWidth: TABLE_MIN_WIDTH,
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "REJECTED":
      return "red";
    case "PENDING":
      return "violet";
    case "CANCELED":
      return "gray";
    default:
      return "gray";
  }
};

const formatDate = (dateString: string) => {
  return DateUtils.formatDateDisplay(dateString);
};

const AbsencesTab = ({ absences, loadCalendar, calendarRefreshKey, onRequestClick, onAbsenceClick, onStatusEditClick, loading, readOnly, isAdmin }: AbsencesTabProps) => {
  const { t } = useTranslation();
  const { displayedDate, events, setDisplayedDate } = useCalendarEvents(loadCalendar, calendarRefreshKey);
  const dayDetailsByDate = useMemo(
    () => CalendarEventUtils.buildCalendarDayDetails(events),
    [events],
  );

  return (
    <Box className={classes.layout}>
      {/* Tabela odsustva */}
      <Box className={classes.tableSection}>
        <Box className={classes.tableWrapper}>
          <Box className={classes.tableInner} style={tableInnerStyle}>
            <Table className={classes.table} verticalSpacing="xs" horizontalSpacing="sm" striped stripedColor="gray.0" bg="gray.2" highlightOnHover highlightOnHoverColor="gray.1">
              <Table.Thead bg="white">
                <Table.Tr>
                  <Table.Th miw={180}>{t("profilePage.absences.table.createdOn")}</Table.Th>
                  <Table.Th miw={140}>{t("profilePage.absences.table.dateFrom")}</Table.Th>
                  <Table.Th miw={140}>{t("profilePage.absences.table.dateTo")}</Table.Th>
                  <Table.Th miw={110}>{t("profilePage.absences.table.workingDays")}</Table.Th>
                  <Table.Th miw={150}>{t("profilePage.absences.table.replacement")}</Table.Th>
                  <Table.Th miw={210}>{t("profilePage.absences.table.status")}</Table.Th>
                  <Table.Th miw={140}>{t("profilePage.absences.table.reason")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Box py="xl">
                        <Loader />
                      </Box>
                    </Table.Td>
                  </Table.Tr>
                ) : absences.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md">{t("profilePage.absences.table.empty")}</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  absences.map((absence) => (
                    <Table.Tr 
                      key={absence.id} 
                      onClick={readOnly ? undefined : () => onAbsenceClick(absence.id)} 
                      style={{ cursor: readOnly ? 'default' : 'pointer' }}
                    >
                      <Table.Td>{absence.createdOn ? formatDate(absence.createdOn) : '-'}</Table.Td>
                      <Table.Td>{formatDate(absence.dateFrom)}</Table.Td>
                      <Table.Td>{formatDate(absence.dateTo)}</Table.Td>
                      <Table.Td>{absence.workingDays}</Table.Td>
                      <Table.Td>{absence.replacementAgent?.fullName || '-'}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text c={getStatusColor(absence.status)} fw={500}>
                            {t(`profilePage.absences.status.${absence.status}`, { defaultValue: absence.status })}
                          </Text>
                          {isAdmin && absence.status === 'PENDING' && onStatusEditClick && (
                            <ActionIcon 
                              variant="transparent" 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                onStatusEditClick(absence);
                              }}
                            >
                              <IconEdit size={16} color="var(--mantine-color-gray-6)" />
                            </ActionIcon>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {absence.reason
                          ? t(`profilePage.absences.reason.${absence.reason}`, { defaultValue: absence.reason })
                          : "-"}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Box>
        {!readOnly && (
          <Flex className={classes.actionsRow} mt="md">
            <Button color="gray" radius="xl" onClick={onRequestClick}>
              {t("profilePage.absences.actions.submitRequest")}
            </Button>
          </Flex>
        )}
      </Box>

      {/* Kalendar */}
      <Box className={classes.calendarSection}>
        <Calendar
          static
          date={displayedDate}
          onDateChange={setDisplayedDate}
          highlightToday={false}
          getDayProps={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);
            const isToday = dateString === DateUtils.formatDateLocal(new Date());

            return {
              style: {
                ...(details?.background && {
                  backgroundColor: details.background,
                  color: "white",
                  borderRadius: 4,
                }),
                ...(isToday && !details?.background && {
                  border: "2px solid var(--mantine-color-gray-5)",
                }),
              },
            };
          }}
          renderDay={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);

            if (details?.tooltipLabels.length) {
              return (
                <Tooltip 
                  label={details.tooltipLabels.map((label) => <div key={label}>{label}</div>)}
                  withArrow
                  multiline
                  position="top"
                >
                  <div>{date.getDate()}</div>
                </Tooltip>
              );
            }
            return date.getDate();
          }}
        />
      </Box>
    </Box>
  );
};

export default AbsencesTab;
